import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton, Paper, Badge } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AlertCard from './AlertCard';
import { VideoWallAlert } from '../../types/canvision.types';

interface AlertOverlayProps {
  alerts: VideoWallAlert[];
  onView: (alert: VideoWallAlert) => void;
  onDismiss: (alertId: number) => void;
  maxVisible?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const POSITION_STYLES = {
  'top-right': { top: 16, right: 16 },
  'top-left': { top: 16, left: 16 },
  'bottom-right': { bottom: 16, right: 16 },
  'bottom-left': { bottom: 16, left: 16 },
};

function AlertOverlay({
  alerts,
  onView,
  onDismiss,
  maxVisible = 3,
  position = 'bottom-right',
}: AlertOverlayProps) {
  const [expanded, setExpanded] = useState(false);

  if (alerts.length === 0) {
    return null;
  }

  const visibleAlerts = expanded ? alerts : alerts.slice(0, maxVisible);
  const hiddenCount = alerts.length - maxVisible;
  const positionStyle = POSITION_STYLES[position];
  const isBottom = position.startsWith('bottom');

  return (
    <Box
      sx={{
        position: 'absolute',
        ...positionStyle,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        // Hide scrollbar but allow scrolling
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Show expand/collapse control when there are hidden alerts */}
      {hiddenCount > 0 && !isBottom && (
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            bgcolor: 'grey.900',
            opacity: 0.85,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Badge badgeContent={hiddenCount} color="error" invisible={expanded}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ color: 'common.white' }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Badge>
          <Typography variant="caption" sx={{ color: 'common.white', ml: 0.5 }}>
            {expanded ? 'Show less' : `+${hiddenCount} more`}
          </Typography>
        </Paper>
      )}

      {/* Alert cards */}
      {visibleAlerts.map((alert, index) => (
        <Collapse key={alert.id} in timeout={300 + index * 100}>
          <AlertCard
            alert={alert}
            onView={onView}
            onDismiss={onDismiss}
            compact={alerts.length > 2}
          />
        </Collapse>
      ))}

      {/* Show expand/collapse control at bottom when position is bottom */}
      {hiddenCount > 0 && isBottom && (
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            bgcolor: 'grey.900',
            opacity: 0.85,
            backdropFilter: 'blur(4px)',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Badge badgeContent={hiddenCount} color="error" invisible={expanded}>
            <IconButton
              size="small"
              sx={{ color: 'common.white' }}
            >
              {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Badge>
          <Typography variant="caption" sx={{ color: 'common.white', ml: 0.5 }}>
            {expanded ? 'Show less' : `+${hiddenCount} more`}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default AlertOverlay;
